/**
 * EventWebhookSettings — /w/:slug/app/settings/event-webhooks (V114-5-2, D-114-11; panel round
 * v114-track5: H2/H6/H7/H8/H9/H10 + mediums).
 *
 * Admin page for the NOTIFICATION webhooks (finding opened / closed and pack-validity changes,
 * HMAC-signed; routes/webhooks.ts) — distinct from Settings → Audit log webhooks (the audit-event
 * stream), cross-linked both ways (H8).
 *
 *   List      each endpoint: name + actions pinned to the header line; URL, secret hint, last
 *             delivery state ("Delivery failed — retries exhausted" for a dead letter).
 *   Add       https URL (+ label); ≤5 surfaced inline (the toggle disables at the limit, H10: the
 *             open form's submit owns the primary); the FULL signing secret shows exactly ONCE with
 *             Copy — dismissing it uncopied asks first (H2), and focus moves onto the banner.
 *   Rotate    behind a confirm naming the consequence (H6); the new secret once.
 *   Delete    ghost-danger at rest; the confirm names the consequence; both dialogs are
 *             aria-described by it (H1).
 *   Test      "Send test event" — a signed webhook.test ping, result shown inline (H7).
 *   Log       per-endpoint drawer (inset): event, attempt, status, next retry; a dead-lettered row
 *             offers "Redeliver" (H7).
 *
 * Every route is owner / admin on the server (requireRole on the router); this page mirrors that:
 * other roles see who can manage it and nothing is fetched (no 403 round-trips).
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, EmptyState, Field, Input, PageHeader, Spinner } from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import {
  useCreateWebhook, useDeleteWebhook, useOutboundWebhooks, useRedeliverWebhook, useRotateWebhookSecret,
  useSendWebhookTest, useWebhookDeliveries,
} from '../hooks/useGaps';
import { BLOCKED_DELIVERY_LABEL, deliveryStateLabel, type OutboundWebhookEndpoint } from '../lib/gaps-api';
import { monitoringErrorOf } from '../lib/monitoring-api';
import { fmtDate } from '../lib/format';
import { ActionDialog, useAnnounce, useFocusAfter } from '../components/today/shared';
import { CopyField, StatusChip, useMemberNames } from '../components/monitoring/parts';
import './monitoring.css';

const NOT_CONFIGURED = 'Webhook signing is not configured for this deployment yet. Contact GAC Pro support (or your self-hosted operator).';

const DELIVERY_TONE: Record<string, { tone: 'ok' | 'amber' | 'red' | 'muted' | 'outline'; glyph: 'check' | 'clock' | 'x' | 'circle' }> = {
  delivered: { tone: 'ok', glyph: 'check' },
  pending: { tone: 'muted', glyph: 'clock' },
  failed: { tone: 'amber', glyph: 'x' },
  dead_letter: { tone: 'red', glyph: 'x' },
};
/** A row the SSRF guard refused at send time (failure_reason, migration 875) reads as blocked, in red. */
const deliveryChip = (status: string, failureReason?: string | null) => (failureReason === 'blocked_destination'
  ? { label: BLOCKED_DELIVERY_LABEL, tone: 'red' as const, glyph: 'x' as const }
  : { label: deliveryStateLabel(status), ...(DELIVERY_TONE[status] ?? { tone: 'outline' as const, glyph: 'circle' as const }) });

export function EventWebhookSettings() {
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug;
  const prefix = `/w/${slug}/app`;
  const isAdmin = role === 'owner' || role === 'admin';
  const q = useOutboundWebhooks(isAdmin);
  const [announcer, announce] = useAnnounce();
  const [adding, setAdding] = useState(false);
  // The one-time secret lives in a ref (never the query cache); the banner copies from it.
  const secretRef = useRef<string | null>(null);
  const [reveal, setReveal] = useState<{ kind: 'created' | 'rotated'; endpointId: string } | null>(null);
  const focusAfter = useFocusAfter();
  useEffect(() => () => { secretRef.current = null; }, []);

  const endpoints = q.data?.endpoints ?? [];
  const limit = q.data?.limit ?? 5;
  const atLimit = endpoints.length >= limit;

  // H10: while the form is open, its own submit is the page's only primary; the header toggle goes quiet and disabled.
  const header = (
    <PageHeader variant="app" title="Notification webhooks" headingId="ewh-h1"
      subtitle="Finding opened / closed and pack-validity changes, signed with each endpoint's secret."
      right={isAdmin && endpoints.length > 0 ? (
        <span className="mon-cell-stack">
          <Button variant={adding ? 'ghost' : 'primary'} compact aria-disabled={adding || atLimit || undefined} aria-expanded={adding}
            aria-describedby={atLimit && !adding ? 'ewh-limit' : undefined}
            onClick={() => { if (!adding && !atLimit) setAdding(true); }}>Add endpoint</Button>
          {atLimit && !adding ? <span id="ewh-limit" className="mon-sub">All {limit} endpoint slots are in use. Delete one first.</span> : null}
        </span>
      ) : undefined} />
  );

  if (!isAdmin) {
    return (
      <div className="mon-page">
        {header}
        <Alert variant="info">Only a workspace owner or admin manages notification webhooks. Ask an owner or admin to add or change endpoints.</Alert>
        <p className="mon-hint">Looking for the audit-event stream? That is <Link to={`${prefix}/settings/webhooks`}>Audit log webhooks</Link>.</p>
      </div>
    );
  }

  return (
    <div className="mon-page">
      {announcer}
      {header}
      {reveal ? (
        <SecretOnceBanner kind={reveal.kind} getSecret={() => secretRef.current}
          onDismiss={() => { secretRef.current = null; setReveal(null); focusAfter('ewh-list-h'); }} />
      ) : null}
      {adding ? (
        <AddEndpointForm
          onCreated={(endpointId, secret) => {
            secretRef.current = secret;
            setReveal({ kind: 'created', endpointId });
            setAdding(false);
            announce('Endpoint added. Copy the signing secret now.');
            focusAfter('ewh-secret-banner');
          }} onCancel={() => setAdding(false)} />
      ) : null}
      {q.loading ? <Spinner label="Loading endpoints" /> : q.error ? (
        <Alert variant="error">Endpoints could not be loaded. <Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>
      ) : !endpoints.length ? (
        <EmptyState title="No notification webhooks yet" description="Add an https endpoint to receive finding and pack-validity events, signed with a secret only you hold."
          action={<Button variant="primary" onClick={() => setAdding(true)}>Add endpoint</Button>} />
      ) : (
        <Card density="compact" id="ewh-list" headingId="ewh-list-h" title="Endpoints" right={<p className="mon-card-note">{endpoints.length} of {limit}</p>}>
          <ul className="mon-rows">
            {endpoints.map((e) => (
              <EndpointRow key={e.endpoint_id} slug={slug} e={e} onAnnounce={announce}
                onRotated={(secret) => { secretRef.current = secret; setReveal({ kind: 'rotated', endpointId: e.endpoint_id }); focusAfter('ewh-secret-banner'); }} />
            ))}
          </ul>
        </Card>
      )}
      <p className="mon-hint">These are the finding and pack-validity notifications. The audit-event stream has its own endpoints under <Link to={`${prefix}/settings/webhooks`}>Audit log webhooks</Link>.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One-time secret banner (H2: focus lands here; dismissing uncopied asks first)
// ---------------------------------------------------------------------------

function SecretOnceBanner({ kind, getSecret, onDismiss }: { kind: 'created' | 'rotated'; getSecret: () => string | null; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const secret = getSecret();
  if (!secret) return null;
  const dismiss = () => {
    if (copied) { onDismiss(); return; }
    setConfirming(true);
  };
  return (
    <div id="ewh-secret-banner" tabIndex={-1} data-part="secret-banner">
      <Alert variant="warning">
        <div className="mon-col">
          <strong>{kind === 'created' ? 'Copy the signing secret now — you won’t see this again.' : 'Copy the new signing secret now — you won’t see this again.'}</strong>
          <CopyField label="Signing secret" value={secret} onCopied={() => setCopied(true)} />
          <p className="mon-sub">GAC Pro keeps only a hash and the last four characters.{kind === 'rotated' ? ' The old secret no longer signs.' : ''}</p>
          <div className="mon-actions"><Button compact onClick={dismiss}>{copied ? 'Done' : 'Dismiss'}</Button></div>
        </div>
      </Alert>
      <ActionDialog open={confirming} title="Dismiss without copying?" submitLabel="Dismiss anyway" danger
        subtitle="The secret is shown only this once. Without it, the receiver cannot verify signatures — you would have to rotate."
        onClose={() => setConfirming(false)}
        onSubmit={() => { setConfirming(false); onDismiss(); return true; }}>
        <p className="mon-hint">Copy it first if the receiver will verify deliveries.</p>
      </ActionDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add
// ---------------------------------------------------------------------------

function AddEndpointForm({ onCreated, onCancel }: { onCreated: (endpointId: string, secret: string) => void; onCancel: () => void }) {
  const uid = useId();
  const create = useCreateWebhook();
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const [urlErr, setUrlErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <Card density="compact" headingId="ewh-add-h" title="Add endpoint">
      <form className="mon-col" noValidate onSubmit={async (e) => {
        e.preventDefault();
        if (create.isPending) return; // medium: no double submit
        const u = url.trim();
        if (!u) { setUrlErr('An https URL is required.'); return; }
        if (!/^https:\/\//i.test(u)) { setUrlErr('The webhook URL must be https.'); return; }
        setError(null);
        try {
          const out = await create.mutateAsync({ url: u, ...(label.trim() ? { label: label.trim() } : {}) });
          setUrl(''); setLabel('');
          onCreated(out.endpoint.endpoint_id, out.secret);
        } catch (err) {
          const f = monitoringErrorOf(err);
          if (f.kind === 'not_configured') setError(NOT_CONFIGURED);
          else if (f.fields.some((x) => x.path === 'url')) setUrlErr(f.fields.find((x) => x.path === 'url')!.message);
          // SSRF guard refusals (private / internal destination, userinfo, unresolvable) land on the URL field in plain words.
          else if (f.code === 'blocked_destination' || f.code === 'invalid_url') setUrlErr(f.message);
          else setError(f.message || 'The endpoint could not be added.');
        }
      }}>
        {error ? <Alert variant="error">{error}</Alert> : null}
        <Field id={`${uid}-url`} label="Endpoint URL" required hint="https only — a Slack incoming-webhook URL works here" error={urlErr ?? undefined}>
          <Input type="url" value={url} placeholder="https://hooks.example.com/gacpro" autoComplete="off"
            onChange={(e) => { setUrl(e.currentTarget.value); setUrlErr(null); }} />
        </Field>
        <Field id={`${uid}-label`} label="Label (optional)">
          <Input value={label} placeholder="e.g. Security Slack channel" autoComplete="off" onChange={(e) => setLabel(e.currentTarget.value)} />
        </Field>
        <div className="mon-actions">
          <Button type="submit" variant="primary" aria-disabled={create.isPending || undefined}>{create.isPending ? 'Adding…' : 'Add endpoint'}</Button>
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        </div>
      </form>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Endpoint row: test, rotate (confirmed), delete (confirmed), delivery log drawer
// ---------------------------------------------------------------------------

function EndpointRow({ slug, e, onAnnounce, onRotated }: {
  slug: string; e: OutboundWebhookEndpoint; onAnnounce: (m: string) => void; onRotated: (secret: string) => void;
}) {
  const rotate = useRotateWebhookSecret();
  const del = useDeleteWebhook();
  const test = useSendWebhookTest();
  const names = useMemberNames(slug);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [confirmingRotate, setConfirmingRotate] = useState(false);
  // Medium: rotate / delete / test errors are their own, cleared when their dialog (or action) starts.
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const last = e.last_delivery;
  const chip = last ? deliveryChip(last.status, last.failure_reason) : null;
  return (
    <li className="mon-row mon-row--block" data-part="webhook-endpoint">
      <span className="mon-row-main">
        {/* Medium: the actions pin to the endpoint's header line. */}
        <span className="mon-line mon-endpoint-head">
          <span className="mon-name">{e.label || e.url}</span>
          <span className="mon-row-acts">
            <Button compact aria-disabled={test.isPending || undefined} onClick={async () => {
              if (test.isPending) return;
              setTestResult(null);
              try {
                const out = await test.mutateAsync(e.endpoint_id);
                setTestResult(out.test.blocked ? (out.test.message ?? 'Not sent: this endpoint points at a private or internal address.')
                  : out.test.delivered ? `Test delivered · HTTP ${out.test.response_code}`
                    : `Test failed${out.test.response_code ? ` · HTTP ${out.test.response_code}` : ' · endpoint unreachable'}`);
                onAnnounce(out.test.blocked ? 'Test event not sent: the destination is blocked' : out.test.delivered ? 'Test event delivered' : 'Test event failed');
              } catch (err) {
                const f = monitoringErrorOf(err);
                setTestResult(f.kind === 'not_configured' ? NOT_CONFIGURED : f.message || 'The test could not be sent.');
              }
            }}>{test.isPending ? 'Sending…' : 'Send test event'}</Button>
            <Button compact onClick={() => { setRotateError(null); setConfirmingRotate(true); }}>Rotate secret</Button>
            {/* Medium: destructive action is ghost-danger at rest. */}
            <Button compact variant="ghost" style={{ color: 'var(--danger-fg)' }} onClick={() => { setDeleteError(null); setConfirmingDelete(true); }}>Delete<span className="gp-sr-only"> {e.label || e.url}</span></Button>
          </span>
        </span>
        <span className="mon-sub">{e.url}</span>
        <span className="mon-line">
          <span className="mon-sub">Secret ends …{e.secret_hint}</span>
          {e.rotated_at ? <span className="mon-sub">rotated {fmtDate(e.rotated_at)}</span> : null}
          <span className="mon-sub">added {fmtDate(e.created_at)}{names.get(e.created_by) ? ` by ${names.get(e.created_by)}` : ''}</span>
        </span>
        <span className="mon-line" data-part="last-delivery">
          {chip && last ? (
            <>
              <StatusChip label={`Last delivery: ${chip.label}`} tone={chip.tone} glyph={chip.glyph} />
              <span className="mon-sub">{last.event}{last.response_code !== null ? ` · HTTP ${last.response_code}` : ''}{last.completed_at ? ` · ${fmtDate(last.completed_at)}` : ''}</span>
            </>
          ) : <span className="mon-sub">No deliveries yet — send a test event to prove the secret.</span>}
        </span>
        {testResult ? <p className="mon-sub" role="status" data-part="test-result">{testResult}</p> : null}
        <DeliveryLog slug={slug} endpointId={e.endpoint_id} context={e.label || e.url} onAnnounce={onAnnounce} />
      </span>
      {/* H6: rotation is destructive for the receiver — same confirm pattern as delete. */}
      <ActionDialog open={confirmingRotate} title={`Rotate the secret for ${e.label || 'this endpoint'}`} submitLabel="Rotate secret" danger
        busy={rotate.isPending} error={rotateError}
        subtitle="The current secret stops signing immediately; deliveries fail until the receiver has the new secret."
        onClose={() => setConfirmingRotate(false)}
        onSubmit={async () => {
          setRotateError(null);
          try {
            const out = await rotate.mutateAsync(e.endpoint_id);
            setConfirmingRotate(false);
            onRotated(out.secret);
            onAnnounce('Secret rotated. Copy the new secret now.');
            return true;
          } catch (err) {
            const f = monitoringErrorOf(err);
            setRotateError(f.kind === 'not_configured' ? NOT_CONFIGURED : f.message || 'Could not rotate the secret.');
            return false;
          }
        }}>
        <p className="mon-hint">The new secret shows once after rotating; hand it to the receiver right away.</p>
      </ActionDialog>
      <ActionDialog open={confirmingDelete} title={`Delete ${e.label || 'this endpoint'}`} submitLabel="Delete endpoint" danger
        busy={del.isPending} error={deleteError}
        subtitle={`Deliveries to ${e.url} stop immediately and its delivery log is removed.`}
        onClose={() => setConfirmingDelete(false)}
        onSubmit={async () => {
          setDeleteError(null);
          try {
            await del.mutateAsync(e.endpoint_id);
            setConfirmingDelete(false);
            onAnnounce('Endpoint deleted');
            return true;
          } catch (err) { setDeleteError(monitoringErrorOf(err).message || 'Could not delete.'); return false; }
        }}>
        <p className="mon-hint">The deletion itself stays in the audit trail.</p>
      </ActionDialog>
    </li>
  );
}

function DeliveryLog({ slug: _slug, endpointId, context, onAnnounce }: { slug: string; endpointId: string; context: string; onAnnounce: (m: string) => void }) {
  const [open, setOpen] = useState(false);
  const q = useWebhookDeliveries(open ? endpointId : null);
  const redeliver = useRedeliverWebhook();
  const [err, setErr] = useState<string | null>(null);
  const deliveries = q.data?.deliveries ?? [];
  return (
    <details className="mon-disclose" data-part="delivery-log" onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}>
      <summary>Delivery log<span className="gp-sr-only"> · {context}</span></summary>
      {!open ? null : q.loading ? <Spinner label="Loading deliveries" /> : q.error ? (
        <Alert variant="error">The delivery log could not be loaded. <Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>
      ) : !deliveries.length ? <p className="mon-hint">No deliveries yet.</p> : (
        <div className="mon-delivery-log">
          {err ? <p className="mon-err" role="alert">{err}</p> : null}
          <ul className="mon-rows">
            {deliveries.map((d) => {
              const chip = deliveryChip(d.status, d.failure_reason);
              return (
                <li key={`${d.delivery_id}-${d.attempt}`} className="mon-row">
                  <span className="mon-row-main">
                    <span className="mon-line">
                      <span className="mon-event">{d.event}</span>
                      <StatusChip label={chip.label} tone={chip.tone} glyph={chip.glyph} />
                    </span>
                    <span className="mon-sub">
                      Attempt {d.attempt} · {fmtDate(d.scheduled_at)}
                      {d.response_code !== null ? ` · HTTP ${d.response_code}` : ''}
                      {d.next_retry_at ? ` · next retry ${fmtDate(d.next_retry_at)}` : ''}
                    </span>
                  </span>
                  {d.status === 'dead_letter' ? (
                    <span className="mon-row-acts">
                      <Button compact aria-disabled={redeliver.isPending || undefined} onClick={async () => {
                        if (redeliver.isPending) return;
                        setErr(null);
                        try {
                          const out = await redeliver.mutateAsync({ endpointId, deliveryId: d.delivery_id });
                          if (out.redelivered.blocked) setErr(out.redelivered.message ?? 'Not sent: this endpoint points at a private or internal address.');
                          onAnnounce(out.redelivered.blocked ? 'Not sent: the destination is blocked' : out.redelivered.delivered ? 'Redelivered' : 'Redelivery failed — see the new attempt row');
                        } catch (e2) {
                          const f = monitoringErrorOf(e2);
                          setErr(f.kind === 'not_configured' ? NOT_CONFIGURED : f.message || 'Could not redeliver.');
                        }
                      }}>{redeliver.isPending ? 'Sending…' : 'Redeliver'}<span className="gp-sr-only"> {d.event} attempt {d.attempt}</span></Button>
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </details>
  );
}
